import { useCallback, useSyncExternalStore } from 'react';
import { VideoJsPlayer } from 'video.js';

/**
 * Subscribes to VideoJS player state using useSyncExternalStore so that
 * isPaused, volume, currentTime, duration, and isExpand are always in sync
 * with the player without manual useState/useEffect wiring.
 */
export function useVideoPlayerState(player: VideoJsPlayer) {
  const subscribePausePlay = useCallback(
    (notify: () => void) => {
      player.on('pause', notify);
      player.on('play', notify);
      player.on('ended', notify);
      return () => {
        player.off('pause', notify);
        player.off('play', notify);
        player.off('ended', notify);
      };
    },
    [player]
  );

  const subscribeVolume = useCallback(
    (notify: () => void) => {
      player.on('volumechange', notify);
      return () => player.off('volumechange', notify);
    },
    [player]
  );

  const subscribeCurrentTime = useCallback(
    (notify: () => void) => {
      player.on('timeupdate', notify);
      player.on('seeking', notify);
      player.on('seeked', notify);
      player.on('loadedmetadata', notify);
      player.on('ended', notify);
      return () => {
        player.off('timeupdate', notify);
        player.off('seeking', notify);
        player.off('seeked', notify);
        player.off('loadedmetadata', notify);
        player.off('ended', notify);
      };
    },
    [player]
  );

  const subscribeDuration = useCallback(
    (notify: () => void) => {
      player.on('loadedmetadata', notify);
      player.on('durationchange', notify);
      return () => {
        player.off('loadedmetadata', notify);
        player.off('durationchange', notify);
      };
    },
    [player]
  );

  const subscribeFullscreen = useCallback(
    (notify: () => void) => {
      player.on('fullscreenchange', notify);
      return () => player.off('fullscreenchange', notify);
    },
    [player]
  );

  const isPaused = useSyncExternalStore(subscribePausePlay, () =>
    player.paused()
  );
  const volume = useSyncExternalStore(subscribeVolume, () => player.volume());
  const currentTime = useSyncExternalStore(subscribeCurrentTime, () =>
    player.currentTime()
  );
  const duration = useSyncExternalStore(
    subscribeDuration,
    () => player.duration() || 0
  );
  const isExpand = useSyncExternalStore(subscribeFullscreen, () =>
    player.isFullscreen()
  );

  return { isPaused, volume, currentTime, duration, isExpand };
}
